import { Icon } from './Icon'

/**
 * The two acts this product cannot take back, and what is left on screen
 * afterwards.
 *
 * Both of them used to end in `window.alert` followed on the next line by
 * `window.location.assign('/')`. The workspace one carried the ONLY statement
 * of the grace period anywhere in the product — how many days it stays
 * restorable, and that support is who brings it back — in a box the operating
 * system drew, over a screen that was already navigating away from underneath
 * it. Whatever it said had nowhere to live: the alert closes, the page
 * reloads, and the terms of the thing that just happened are gone. Nobody can
 * re-read them, screenshot them, or find them again; there is no other screen
 * in the product that states them.
 *
 * Which is why this is a screen rather than a state on the card that started
 * it. The card is inside the workspace, and the workspace no longer resolves:
 * the next poll answers 403, the shell notices it has lost access, and it
 * would replace Settings with another workspace or with the sign-in gate
 * within a second — taking the confirmation with it. So this outranks all of
 * that at the top of the app, and the person leaves it when they have read it.
 */
export type Deletion =
  { kind: 'workspace'; name: string; graceDays: number } | { kind: 'account'; email: string }

/**
 * The day the grace period runs out, as a date rather than a duration.
 *
 * "Restorable for 30 days" is only answerable by somebody who also knows what
 * day the deletion happened — and the one person who needs the answer is
 * reading this in a hurry, often because it was the wrong workspace. A date
 * survives being written on a sticky note; "30 days" does not.
 */
function lastDay(graceDays: number): string {
  const day = new Date(Date.now() + graceDays * 24 * 60 * 60 * 1000)
  return day.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

export function Deleted({ what, onLeave }: { what: Deletion; onLeave: () => void }) {
  return (
    <div className="gate">
      {/* The identity block the sign-in screen and the no-workspace screen
          both use. This IS the way out of the product, and it should read as
          the same room as the way in. The heading below is an `h2` for the
          same reason theirs are not `h1`s: `.gate h1` is the wordmark, and it
          is lowercased. */}
      <div className="gate__identity">
        <span className="gate__mark">
          <Icon name="logo" size={26} />
        </span>
        <h1>Staleless</h1>
      </div>

      {what.kind === 'workspace' ? (
        <div className="farewell" data-deleted="workspace">
          <h2 className="farewell__title">
            <Icon name="trash" size={17} />
            {what.name} is deleted
          </h2>
          <p className="farewell__text">
            Readers can no longer reach it. Every documentation site in it stopped answering the
            moment you pressed the button, and the domains were detached at the same time.
          </p>
          {/* The load-bearing paragraph, and the reason this screen exists.
              Drawn as the product's "read this before you leave" block rather
              than as another paragraph in the same grey, because somebody who
              has just deleted the wrong workspace has about one sentence of
              attention and this is the one that gets it back. */}
          <p className="farewell__terms">
            <Icon name="history" size={15} />
            <span>
              It stays restorable until <strong>{lastDay(what.graceDays)}</strong> —{' '}
              {what.graceDays} days from today. Ask support before then and they can bring the whole
              workspace back: the pages, the drafts, the history and every version. After that day
              it is gone, and nobody can bring it back.
            </span>
          </p>
          <p className="farewell__text">
            Your content repository was never touched by any of this. It is yours, and everything
            that was ever published from it is still in it.
          </p>
          <div className="farewell__actions">
            <button className="btn btn--primary" data-leave onClick={onLeave}>
              Continue
            </button>
          </div>
        </div>
      ) : (
        <div className="farewell" data-deleted="account">
          <h2 className="farewell__title">
            <Icon name="trash" size={17} />
            Your account is deleted
          </h2>
          <p className="farewell__text">
            You are signed out here, and on every other device that was signed in. The address{' '}
            <code>{what.email}</code> is free again — signing up with it makes a new person, not
            this one.
          </p>
          {/* The workspace keeps a grace period; a person does not. Said
              here because it is the difference between the two acts, and the
              alert that used to stand in this spot said neither. */}
          <p className="farewell__terms">
            <Icon name="alert" size={15} />
            <span>
              There is no grace period on an account and nothing for support to restore. This one is
              gone.
            </span>
          </p>
          <p className="farewell__text">
            The teams&rsquo; work stays. Pages, drafts and history keep your name on what you wrote,
            and the files you uploaded are still in their libraries — deleting a person never
            deletes the documentation.
          </p>
          <div className="farewell__actions">
            <button className="btn btn--primary" data-leave onClick={onLeave}>
              Go to the sign-in page
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
